import { useCallback, useState } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { RespondentType } from '../generated/referral-request';
import { toReferralRequestRespondents, type ReferralRequestFormValues } from '../referral-request-form-utils';
import { type FormValues, getRespondentFormValue } from '../respondent-fields';

type UseReferralRequestFormHandlersParams = {
  readonly form: Pick<UseFormReturn<FormValues>, 'getValues' | 'trigger'>;
  readonly onSaveDraft: (values: ReferralRequestFormValues) => Promise<void> | void;
  readonly respondentTypes: RespondentType[];
};

const getPresentRespondentPhoneFieldNames = (values: FormValues, respondentTypes: RespondentType[]) =>
  respondentTypes
    .filter(
      (respondentType) => getRespondentFormValue(values.respondents[respondentType]).phoneNumber.trim().length > 0
    )
    .map((respondentType) => `respondents.${respondentType}.phoneNumber` as const);

const useReferralRequestFormHandlers = ({
  form,
  onSaveDraft,
  respondentTypes,
}: UseReferralRequestFormHandlersParams) => {
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const { getValues, trigger } = form;

  const saveDraftHandler = useCallback(async () => {
    setIsSavingDraft(true);
    try {
      const values = getValues();
      const presentPhoneFieldNames = getPresentRespondentPhoneFieldNames(values, respondentTypes);

      if (presentPhoneFieldNames.length > 0 && !(await trigger(presentPhoneFieldNames))) return;

      await onSaveDraft({
        patientId: values.patientId,
        respondents: toReferralRequestRespondents(values.respondents, respondentTypes),
      });
    } finally {
      setIsSavingDraft(false);
    }
  }, [getValues, onSaveDraft, respondentTypes, trigger]);

  return { isSavingDraft, saveDraftHandler };
};

export { useReferralRequestFormHandlers };
